import type { FC } from 'react';
import React, { useCallback, useEffect, useState, useRef } from 'react';
import { defineMessages, useIntl } from 'react-intl';
import { css, cssMap, keyframes } from '@compiled/react';

import { Inline } from '@atlaskit/primitives/compiled/inline';
import { Pressable } from '@atlaskit/primitives/compiled/pressable';
import { Text } from '@atlaskit/primitives/compiled/text';
import { Stack } from '@atlaskit/primitives/compiled/stack';
import Button from '@atlaskit/button/default/button';
import IconButton from '@atlaskit/button/icon/button';
import ChevronDownIcon from '@atlaskit/icon/core/chevron-down';
import ChevronRightIcon from '@atlaskit/icon/core/chevron-right';
import EyeOpenIcon from '@atlaskit/icon/core/eye-open';
import EyeOpenStrikethroughIcon from '@atlaskit/icon/core/eye-open-strikethrough';
import LockLockedIcon from '@atlaskit/icon/core/lock-locked';
import ThumbsDownIcon from '@atlaskit/icon/core/thumbs-down';
import ThumbsUpIcon from '@atlaskit/icon/core/thumbs-up';
import { token } from '@atlaskit/tokens';
import Tooltip from '@atlaskit/tooltip/Tooltip';
import { aiSuggestionsMessages } from '@atlaskit/editor-common/ai-messages/ai-suggestions';

import type { Suggestion } from '@atlassian/editor-plugin-ai-suggestions';

import { useReasoningViewedAnalytics } from '../analytics.ts';
import { SuggestionAgentIdentity } from './SuggestionAgentIdentity.tsx';
import { SuggestionItemSources } from './SuggestionItemSources.tsx';

const i18n = defineMessages({
	showReasoning: {
		id: 'suggestions-panel.show-reasoning',
		defaultMessage: 'Show reasoning',
		description: 'Label for the button that expands the reasoning section of a suggestion card',
	},
	hideReasoning: {
		id: 'suggestions-panel.hide-reasoning',
		defaultMessage: 'Hide reasoning',
		description: 'Label for the button that collapses the reasoning section of a suggestion card',
	},
	accept: {
		id: 'suggestions-panel.accept',
		defaultMessage: 'Accept',
		description: 'Label for the button that accepts a suggestion',
	},
	discard: {
		id: 'suggestions-panel.discard',
		defaultMessage: 'Discard',
		description: 'Label for the button that discards a suggestion',
	},
	goodSuggestion: {
		id: 'suggestions-panel.good-suggestion',
		defaultMessage: 'Good suggestion',
		description:
			'Accessible label / tooltip for the thumbs up button that submits positive feedback on a suggestion',
	},
	badSuggestion: {
		id: 'suggestions-panel.bad-suggestion',
		defaultMessage: 'Bad suggestion',
		description:
			'Accessible label / tooltip for the thumbs down button that submits negative feedback on a suggestion',
	},
	dismiss: {
		id: 'suggestions-panel.dismiss',
		defaultMessage: 'Dismiss',
		description: 'Label for the button that dismisses a suggestion',
	},
	privateLabel: {
		id: 'suggestions-panel.private-label',
		defaultMessage: 'Private',
		description: 'Accessible label for the padlock icon on the suggestion card',
	},
	privateTooltipShort: {
		id: 'suggestions-panel.private-tooltip-short',
		defaultMessage: 'Visible only to you',
		description: 'Tooltip for the padlock icon on the suggestion card',
	},
});

export const SUGGESTION_ITEM_DESCRIPTION_SHIMMER_TESTID = 'suggestion-item-description-shimmer';

// Shimmer applied to the description while the suggestion is regenerating.
// Copied from the editor's SuggestionCard (itself a copy of
// rovo-platform-ui-components' ShimmerText) rather than using `ShimmerText`
// directly: that component hardcodes its own Text size/colour, which would
// change the description's typography. This wraps the existing Text untouched.
const shimmerAnimation = keyframes({
	'0%': {
		backgroundPosition: '200% 0',
	},
	'100%': {
		backgroundPosition: '-200% 0',
	},
});

const shimmerGradient = css({
	background: `linear-gradient(
		90deg,
		${token('color.text.subtlest')} 0%,
		${token('color.text.subtlest')} 20%,
		#c7cddc 40%,
		${token('color.text.subtlest')} 60%,
		${token('color.text.subtlest')} 80%,
		#c7cddc 90%,
		${token('color.text.subtlest')} 100%
	)`,
	backgroundSize: '200% 100%',
	WebkitBackgroundClip: 'text',
	WebkitTextFillColor: 'transparent',
	backgroundClip: 'text',
	animation: `${shimmerAnimation} 4s linear infinite`,
});

const styles = cssMap({
	item: {
		border: 'none',
		paddingInline: token('space.300'),
		paddingBlock: token('space.300'),
		backgroundColor: token('elevation.surface.raised'),
		cursor: 'pointer',
		display: 'flex',
		flexDirection: 'column',
		alignItems: 'start',
		justifyContent: 'start',
		textAlign: 'start',
		gap: token('space.200'),
		transition: 'background-color 0.2s ease-in-out',
		'&:hover': {
			backgroundColor: token('elevation.surface.raised.hovered'),
		},
	},
	itemSelected: {
		cursor: 'auto',
		backgroundColor: token('elevation.surface.sunken'),
		'&:hover': {
			backgroundColor: token('elevation.surface.sunken'),
		},
	},
	reasoningToggle: {
		display: 'flex',
		alignItems: 'center',
		color: token('color.text.subtlest'),
		backgroundColor: 'transparent',
		paddingBlock: token('space.100'),
		paddingInline: token('space.0'),
		cursor: 'pointer',
		border: 'none',
	},
	cardRow: {
		width: '100%',
	},
	privateMetadata: {
		flexShrink: 0,
	},
});

export type SuggestionItemProps = {
	suggestion: Suggestion;
	isSelected?: boolean;
	/**
	 * The suggestion is being regenerated (stale check → regenerate). While true,
	 * the description shimmers and the action buttons are disabled so the user
	 * can't act on about-to-be-replaced content.
	 */
	isRegenerating?: boolean;
	onSelect?: (id: string) => void;
	onAccept?: (id: string) => void;
	onDiscard?: (id: string) => void;
	onDismiss?: (id: string) => void;
	onGoodSuggestion?: (id: string) => void;
	onBadSuggestion?: (id: string) => void;
	onToggleDeletedDiffs?: (id: string, hideDeletedDiffs: boolean) => void;
};

export const SuggestionItem: FC<SuggestionItemProps> = ({
	suggestion,
	isSelected = false,
	isRegenerating = false,
	onSelect,
	onAccept,
	onDiscard,
	onDismiss,
	onGoodSuggestion,
	onBadSuggestion,
	onToggleDeletedDiffs,
}) => {
	const ref = useRef<HTMLButtonElement>(null);
	useEffect(() => {
		/**
		 * Scroll the suggestion item into view if it is selected and it's out of view.
		 * Useful for when the sidebar is opened and a suggestion is selected via the Editor.
		 */
		if (isSelected && ref.current) {
			const rect = ref.current.getBoundingClientRect();
			const panelContainer = document.querySelector(
				'[data-testid="suggestions-panel-list-container"]',
			);
			const panelRect = panelContainer?.getBoundingClientRect();
			if (!panelRect || !(rect.top >= panelRect.top && rect.bottom <= panelRect.bottom)) {
				ref.current.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
			}
		}
	}, [isSelected]);
	const { formatMessage } = useIntl();
	const [isReasoningExpanded, setIsReasoningExpanded] = useState(false);
	const fireReasoningViewedAnalytics = useReasoningViewedAnalytics();

	// An out-of-date suggestion can no longer be safely applied — only discard.
	const isStale = suggestion.status === 'STALE';

	const [isHidingDeletedDiffs, setIsHidingDeletedDiffs] = useState(false);
	useEffect(() => {
		if (!isSelected) {
			setIsHidingDeletedDiffs(false);
		}
	}, [isSelected]);

	const handleReasoningToggle = useCallback(
		(e: React.MouseEvent) => {
			// Prevent toggling selection when clicking the reasoning toggle
			e.stopPropagation();
			if (!isReasoningExpanded) {
				fireReasoningViewedAnalytics(suggestion);
			}
			setIsReasoningExpanded((prev) => !prev);
		},
		[fireReasoningViewedAnalytics, isReasoningExpanded, suggestion],
	);

	const handleSelect = useCallback(() => {
		// Once selected, users have to select another suggestion to deselect
		if (isSelected) return;
		onSelect?.(suggestion.id);
	}, [onSelect, suggestion.id, isSelected]);

	const handleAccept = useCallback(
		(e: React.MouseEvent) => {
			e.stopPropagation();
			onAccept?.(suggestion.id);
		},
		[onAccept, suggestion.id],
	);

	const handleDiscard = useCallback(
		(e: React.MouseEvent) => {
			e.stopPropagation();
			onDiscard?.(suggestion.id);
		},
		[onDiscard, suggestion.id],
	);

	const handleDismiss = useCallback(
		(e: React.MouseEvent) => {
			e.stopPropagation();
			onDismiss?.(suggestion.id);
		},
		[onDismiss, suggestion.id],
	);

	const handleGoodSuggestion = useCallback(
		(e: React.MouseEvent) => {
			e.stopPropagation();
			onGoodSuggestion?.(suggestion.id);
		},
		[onGoodSuggestion, suggestion.id],
	);

	const handleBadSuggestion = useCallback(
		(e: React.MouseEvent) => {
			e.stopPropagation();
			onBadSuggestion?.(suggestion.id);
		},
		[onBadSuggestion, suggestion.id],
	);

	const handleToggleDeletedDiffs = useCallback(
		(e: React.MouseEvent) => {
			e.stopPropagation();
			const updatedHideDeletedDiffs = !isHidingDeletedDiffs;
			setIsHidingDeletedDiffs(updatedHideDeletedDiffs);
			onToggleDeletedDiffs?.(suggestion.id, updatedHideDeletedDiffs);
		},
		[isHidingDeletedDiffs, onToggleDeletedDiffs, suggestion.id],
	);

	const isInsight = suggestion.actionKind === 'INFORMATIONAL';

	return (
		<button
			ref={ref}
			css={[styles.item, isSelected && styles.itemSelected]}
			onClick={handleSelect}
			aria-pressed={isSelected}
		>
			<Stack space="space.050" xcss={styles.cardRow}>
				<Text size="large" weight="medium">
					{suggestion.shortDescription}
				</Text>
				<Inline space="space.075" alignBlock="center" separator="·">
					<SuggestionAgentIdentity agent={suggestion.createdByAgent} />
					<div css={styles.privateMetadata}>
						<Tooltip
							position="top"
							content={formatMessage(i18n.privateTooltipShort)}
							ignoreTooltipPointerEvents
						>
							<Inline as="span" space="space.050" alignBlock="center">
								<LockLockedIcon label="" size="small" color={token('color.icon.subtle')} />
								<Text size="small" weight="regular" color="color.text.subtle" align="center">
									{formatMessage(i18n.privateLabel)}
								</Text>
							</Inline>
						</Tooltip>
					</div>
				</Inline>
			</Stack>
			<Stack space="space.050">
				{suggestion.description &&
					(isRegenerating ? (
						<div css={shimmerGradient} data-testid={SUGGESTION_ITEM_DESCRIPTION_SHIMMER_TESTID}>
							<Text as="p">{suggestion.description}</Text>
						</div>
					) : (
						<Text as="p">{suggestion.description}</Text>
					))}
				{isSelected && suggestion.reasoning && (
					<Stack space="space.050">
						<Pressable
							xcss={styles.reasoningToggle}
							onClick={handleReasoningToggle}
							isDisabled={isRegenerating}
						>
							<Inline as="span" space="space.075" alignBlock="center">
								<Text size="small" weight="regular" color="inherit">
									{isReasoningExpanded
										? formatMessage(i18n.hideReasoning)
										: formatMessage(i18n.showReasoning)}
								</Text>
								{isReasoningExpanded ? (
									<ChevronDownIcon label="" size="small" />
								) : (
									<ChevronRightIcon label="" size="small" />
								)}
							</Inline>
						</Pressable>
						{isReasoningExpanded && (
							<Text as="p" color="color.text.subtle">
								{suggestion.reasoning}
							</Text>
						)}
					</Stack>
				)}
			</Stack>
			{isSelected && !!suggestion.sources?.length && (
				<SuggestionItemSources sources={suggestion.sources} />
			)}
			{isSelected && (
				<Inline alignBlock="center" spread="space-between" xcss={styles.cardRow}>
					<Inline space="space.050" alignBlock="center">
						{isInsight ? (
							<Button onClick={handleDismiss} isDisabled={isRegenerating}>
								{formatMessage(i18n.dismiss)}
							</Button>
						) : (
							<React.Fragment>
								{/* A stale suggestion can only be discarded — hide Accept. */}
								{!isStale && (
									<Button appearance="primary" onClick={handleAccept} isDisabled={isRegenerating}>
										{formatMessage(i18n.accept)}
									</Button>
								)}
								<Button
									appearance={isStale ? 'primary' : 'subtle'}
									onClick={handleDiscard}
									isDisabled={isRegenerating}
								>
									{formatMessage(i18n.discard)}
								</Button>
							</React.Fragment>
						)}
					</Inline>
					{!isInsight && (onToggleDeletedDiffs || onGoodSuggestion || onBadSuggestion) && (
						<Inline space="space.050" alignBlock="center">
							{onToggleDeletedDiffs && (
								<Tooltip
									content={
										isHidingDeletedDiffs
											? formatMessage(aiSuggestionsMessages.cardShowDeletedDiffsLabel)
											: formatMessage(aiSuggestionsMessages.cardHideDeletedDiffsLabel)
									}
								>
									<IconButton
										icon={isHidingDeletedDiffs ? EyeOpenStrikethroughIcon : EyeOpenIcon}
										label={
											isHidingDeletedDiffs
												? formatMessage(aiSuggestionsMessages.cardShowDeletedDiffsLabel)
												: formatMessage(aiSuggestionsMessages.cardHideDeletedDiffsLabel)
										}
										appearance="subtle"
										spacing="default"
										onClick={handleToggleDeletedDiffs}
										isDisabled={isRegenerating}
									/>
								</Tooltip>
							)}
							{onGoodSuggestion && (
								<Tooltip content={formatMessage(i18n.goodSuggestion)}>
									<IconButton
										icon={ThumbsUpIcon}
										label={formatMessage(i18n.goodSuggestion)}
										appearance="subtle"
										spacing="default"
										onClick={handleGoodSuggestion}
										isDisabled={isRegenerating}
									/>
								</Tooltip>
							)}
							{onBadSuggestion && (
								<Tooltip content={formatMessage(i18n.badSuggestion)}>
									<IconButton
										icon={ThumbsDownIcon}
										label={formatMessage(i18n.badSuggestion)}
										appearance="subtle"
										spacing="default"
										onClick={handleBadSuggestion}
										isDisabled={isRegenerating}
									/>
								</Tooltip>
							)}
						</Inline>
					)}
				</Inline>
			)}
		</button>
	);
};
